import React, {useCallback, useRef, useState} from "react";
import {Link} from "react-router-dom";
import {useAuthUrl} from "../utils/authRedirect";
import {usePostHog} from "@posthog/react";
import {
  Container,
  InputGroup,
  InputGroupText,
  Button,
  ButtonGroup,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Card,
  CardBody,
  Alert,
  Spinner,
} from "reactstrap";
import {AxiosError} from "axios";
import {Tooltip as ReactTooltip} from "react-tooltip";
import requests from "../requests";
import ShapeCanvas, {ShapeCanvasHandle} from "./ShapeCanvas";
import ShapeImageUpload, {ShapeImageUploadHandle} from "./ShapeImageUpload";
import TickerSeriesPicker, {TickerSeriesMeta, TickerSeriesPickerHandle} from "./TickerSeriesPicker";
import {WINDOW_LABELS_LONG as WINDOW_LABELS} from "../utils/windows";
import {processQueryResponse} from "../utils/processQueryResponse";
import {SHAPE_MIN_POINTS} from "../utils/shapeQuery";

export type ShapeInputMode = "draw" | "upload" | "ticker";

/** What produced the currently-displayed results — handed up so the results panel can
 * label the comparison honestly (see ShapeResultsSummary) without re-deriving it. */
export interface ShapeResultContext {
  mode: ShapeInputMode;
  /** Display window size in trading days (10/20/65/130/260). */
  window: number;
  /** Committed forward horizon in trading days. */
  forecastWindow: number;
  /** Analogs actually returned at that horizon (post attrition + filters). */
  matchCount: number;
  /** The Max Matches the query asked for — the ceiling matchCount fell short of, if it did. */
  requestedMatches: number;
  series: TickerSeriesMeta | null;
}

interface Props {
  setMatches: React.Dispatch<React.SetStateAction<MatchesState[]>>;
  setQuery: React.Dispatch<React.SetStateAction<StockChartQuery>>;
  setForwardDistribution?: React.Dispatch<React.SetStateAction<ForwardDistribution | null>>;
  setForwardCurve?: React.Dispatch<React.SetStateAction<ForwardCurve | null>>;
  setResultContext?: React.Dispatch<React.SetStateAction<ShapeResultContext | null>>;
  onFetchingChange?: (fetching: boolean) => void;
}

// forecastWindow mirrors QueryMatcher's WINDOW_DEFAULTS (same per-window horizon as the
// real-ticker Matcher). score does NOT: a hand-drawn/digitized/resampled-real-series shape
// is far less precise than a real historical ticker window's own query vector, so
// QueryMatcher's real-ticker score defaults (80-99) return few or no matches here for ANY
// of the three input modes — a uniform, much lower default lets a first-time user actually
// see results instead of an empty "no matches" page. The slider still overrides.
const SHAPE_DEFAULT_SCORE = 35;
const WINDOW_DEFAULTS: Record<number, {score: number; forecastWindow: number}> = {
  10: {score: SHAPE_DEFAULT_SCORE, forecastWindow: 1},
  20: {score: SHAPE_DEFAULT_SCORE, forecastWindow: 3},
  65: {score: SHAPE_DEFAULT_SCORE, forecastWindow: 5},
  130: {score: SHAPE_DEFAULT_SCORE, forecastWindow: 10},
  260: {score: SHAPE_DEFAULT_SCORE, forecastWindow: 20},
};

// Forward horizons in TRADING days. `forecast_window` is measured in real trading days on
// every window: get_query_data_and_date step-samples only the historical window and takes
// the forward rows daily (`iloc[window*step : window*step + forecast_window]`), so 21/42/63
// really are ~1/2/3 months regardless of the window's step.
const HORIZON_PRESETS: {label: string; days: number}[] = [
  {label: "1 mo", days: 21},
  {label: "2 mo", days: 42},
  {label: "3 mo", days: 63},
];
const MAX_FORECAST_WINDOW = 63;

function defaultScoreFor(windowValue: number): number {
  return WINDOW_DEFAULTS[windowValue].score;
}

export default function DrawShapeQuery(props: Props) {
  const authUrlFor = useAuthUrl();
  const {
    setMatches,
    setQuery,
    setForwardDistribution,
    setForwardCurve,
    setResultContext,
    onFetchingChange,
  } = props;
  const [mode, setMode] = useState<ShapeInputMode>("draw");
  const [shape, setShape] = useState<number[]>([]);
  const [seriesMeta, setSeriesMeta] = useState<TickerSeriesMeta | null>(null);
  const [windowValue, setWindow] = useState<number>(20);
  const [windowDropdownOpen, setWindowDropdownOpen] = useState<boolean>(false);
  const [score, setScore] = useState<number>(WINDOW_DEFAULTS[20].score);
  const [forecastWindow, setForecastWindow] = useState<number>(WINDOW_DEFAULTS[20].forecastWindow);
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxChange, setMaxChange] = useState<number>(100);
  const [numMatches, setNumMatches] = useState<number>(30);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [errorColor, setErrorColor] = useState<"danger" | "warning">("danger");
  const [noResults, setNoResults] = useState(false);
  const canvasRef = useRef<ShapeCanvasHandle>(null);
  const imageUploadRef = useRef<ShapeImageUploadHandle>(null);
  const tickerSeriesRef = useRef<TickerSeriesPickerHandle>(null);
  const posthog = usePostHog();

  const handleQueryError = useCallback((err: unknown) => {
    if (err instanceof AxiosError) {
      const status = err.response?.status;
      if (status === 401) {
        setErrorColor("danger");
        setError(
          <>
            Please <Link to={authUrlFor("/login")}>log in</Link> to draw a shape query.
          </>
        );
      } else if (status === 429) {
        setErrorColor("warning");
        const detail = err.response?.data?.detail as string | undefined;
        posthog.capture("shape_query_rate_limited", {window: windowValue});
        setError(
          <>
            {detail || "You've reached your daily query limit."}{" "}
            <Link to="/pricing">Upgrade to Pro</Link> for unlimited access.
          </>
        );
      } else if (status === 400) {
        setErrorColor("danger");
        setError(err.response?.data?.error || "Draw a shape with at least a couple of points first.");
      } else if (status === 500) {
        setErrorColor("danger");
        setError("Server error occurred. Please try again later.");
      } else {
        setErrorColor("danger");
        setError(err.response?.data?.error || err.response?.data?.message || "An unexpected error occurred.");
      }
    } else {
      setErrorColor("danger");
      setError("An unexpected error occurred.");
    }
  }, [posthog, windowValue, authUrlFor]);

  const onSubmit = useCallback(async () => {
    if (shape.length < SHAPE_MIN_POINTS) return;
    setError(null);
    setNoResults(false);
    try {
      setFetching(true);
      onFetchingChange?.(true);
      const resp = await requests.post(
        "/api/query/shape",
        {
          shape,
          // Ticker + Lookback mode also sends the REAL prices/dates it loaded,
          // alongside the 0-100-scaled `shape` used for matching, so the result
          // card can show what the security actually traded at instead of the
          // scaled stand-in with a fabricated date (see resources/query.py's
          // _parse_real_series). Matching itself is unaffected either way.
          ...(mode === "ticker" && seriesMeta
            ? {
              real_ticker: seriesMeta.ticker,
              real_prices: seriesMeta.realPrices,
              real_dates: seriesMeta.realDates,
            }
            : {}),
        },
        {params: {window: windowValue, numMatches, minScore: score, forecastWindow, minPrice, maxChange}},
      );
      const d = resp.data as APIQueryResponse;
      const {newQuery, newMatches} = processQueryResponse(d);
      setQuery(newQuery);
      setMatches(newMatches);
      setForwardDistribution?.(d.forward_distribution ?? null);
      setForwardCurve?.(d.forward_curve ?? null);
      setResultContext?.({
        mode,
        window: windowValue,
        forecastWindow,
        matchCount: newMatches.length,
        requestedMatches: numMatches,
        series: mode === "ticker" ? seriesMeta : null,
      });
      posthog.capture("shape_query_submitted", {
        window: windowValue,
        num_matches: newMatches.length,
        min_score: score,
        forecast_window: forecastWindow,
        points_drawn: shape.length,
        input_mode: mode,
        series_ticker: mode === "ticker" ? seriesMeta?.ticker : undefined,
        series_lookback: mode === "ticker" ? seriesMeta?.lookbackPeriod : undefined,
      });
      if (newMatches.length === 0) {
        setNoResults(true);
        setShowAdvanced(true);
      } else {
        setShowAdvanced(false);
      }
    } catch (err: unknown) {
      handleQueryError(err);
      setMatches([]);
      setQuery({} as StockChartQuery);
      setForwardDistribution?.(null);
      setForwardCurve?.(null);
      setResultContext?.(null);
    } finally {
      setFetching(false);
      onFetchingChange?.(false);
    }
  }, [
    shape,
    windowValue,
    numMatches,
    score,
    forecastWindow,
    minPrice,
    maxChange,
    mode,
    seriesMeta,
    setMatches,
    setQuery,
    setForwardDistribution,
    setForwardCurve,
    setResultContext,
    handleQueryError,
    onFetchingChange,
    posthog,
  ]);

  const scoreHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setScore(value);
  };
  const priceHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setMinPrice(value);
  };
  const maxChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setMaxChange(value);
  };
  const numMatchesHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setNumMatches(value);
  };
  const forecastWindowHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) setForecastWindow(Math.min(Math.max(value, 1), MAX_FORECAST_WINDOW));
  };

  // The ticker picker hands up its own metadata alongside the shape; the other two
  // modes have none, so seriesMeta is cleared whenever they emit.
  const handleSeriesChange = useCallback((next: number[], meta: TickerSeriesMeta | null) => {
    setShape(next);
    setSeriesMeta(meta);
  }, []);
  const handleDrawnChange = useCallback((next: number[]) => {
    setShape(next);
    setSeriesMeta(null);
  }, []);

  const clearShape = () => {
    canvasRef.current?.clear();
    imageUploadRef.current?.clear();
    tickerSeriesRef.current?.clear();
    setNoResults(false);
    setError(null);
  };

  // Only one of the three inputs is mounted at a time, so switching modes wouldn't
  // otherwise reset the OTHERS' leftover shape — clear all three explicitly so a
  // shape produced in the mode being left can't silently carry over.
  const switchMode = (next: ShapeInputMode) => {
    if (next === mode) return;
    canvasRef.current?.clear();
    imageUploadRef.current?.clear();
    tickerSeriesRef.current?.clear();
    setMode(next);
    // Reset to this window's default score on every mode switch, same as an explicit
    // window change — a leftover score from the mode being left shouldn't carry over.
    setScore(defaultScoreFor(windowValue));
    setNoResults(false);
    setError(null);
  };

  const submitDisabled = shape.length < SHAPE_MIN_POINTS || fetching;
  const windowDefaultHorizon = WINDOW_DEFAULTS[windowValue].forecastWindow;
  const emptyHint =
    mode === "draw"
      ? "Draw a shape to enable matching."
      : mode === "upload"
        ? "Upload a chart image to enable matching."
        : "Load a security's price history to enable matching.";
  const filledHint = `${shape.length} points ${
    mode === "draw" ? "drawn" : mode === "upload" ? "extracted" : "sampled"
  }`;

  return (
    <Container className="query-matcher-container draw-shape-container">
      <div className="query-header">
        <h1 className="page-title">Draw a Shape</h1>
        <p>
          Sketch any price shape — a dip, a breakout, a double top, whatever you&apos;re picturing —
          upload a chart screenshot, or point it at a real security&apos;s price history, and see the
          historical analogs the same DTW+cosine engine behind the Query Matcher retrieves for it,
          and what happened after each one.
        </p>
      </div>
      <Card className="query-card">
        <CardBody>
          <div className="shape-mode-toggle">
            <Button size="sm" color={mode === "draw" ? "primary" : "outline-secondary"} onClick={() => switchMode("draw")}>
              Draw
            </Button>{" "}
            <Button size="sm" color={mode === "upload" ? "primary" : "outline-secondary"} onClick={() => switchMode("upload")}>
              Upload Image
            </Button>{" "}
            <Button size="sm" color={mode === "ticker" ? "primary" : "outline-secondary"} onClick={() => switchMode("ticker")}>
              Ticker + Lookback
            </Button>
          </div>

          {mode === "draw" && (
            <div className="shape-canvas-wrap">
              <ShapeCanvas ref={canvasRef} onChange={handleDrawnChange} />
            </div>
          )}
          {mode === "upload" && <ShapeImageUpload ref={imageUploadRef} onChange={handleDrawnChange} />}
          {mode === "ticker" && <TickerSeriesPicker ref={tickerSeriesRef} onChange={handleSeriesChange} />}

          <div className="shape-canvas-toolbar">
            <Button color="secondary" outline size="sm" onClick={clearShape} disabled={shape.length === 0}>
              Clear
            </Button>
            <span className="shape-canvas-hint">
              {shape.length < SHAPE_MIN_POINTS ? emptyHint : filledHint}
            </span>
          </div>

          <div className="query-main-row mt-3">
            <InputGroup className="custom-input-group">
              <InputGroupText
                data-tooltip-id="shape-score-tooltip"
                data-tooltip-content="Minimum similarity score (0-100%) for returned matches"
              >
                Min Score
              </InputGroupText>
              <ReactTooltip id="shape-score-tooltip" />
              <input
                className="custom-range-input"
                type="range"
                min="1"
                max="100"
                value={score}
                onChange={scoreHandler}
              />
              <InputGroupText className="range-value">{score}%</InputGroupText>
            </InputGroup>

            <InputGroup className="custom-input-group">
              <InputGroupText
                data-tooltip-id="shape-window-size"
                data-tooltip-content="Number of trading days the shape represents"
              >
                Horizon
              </InputGroupText>
              <ReactTooltip id="shape-window-size" />
              <Dropdown
                isOpen={windowDropdownOpen}
                toggle={() => setWindowDropdownOpen((prev) => !prev)}
                className="custom-dropdown"
              >
                <DropdownToggle caret>{WINDOW_LABELS[windowValue] ?? `${windowValue} days`}</DropdownToggle>
                <DropdownMenu>
                  {[10, 20, 65, 130, 260].map((v) => (
                    <DropdownItem
                      key={v}
                      active={v === windowValue}
                      onClick={() => {
                        setWindow(v);
                        setScore(defaultScoreFor(v));
                        // Keep an explicitly-chosen 1/2/3-month horizon across a window
                        // change; otherwise fall back to that window's own default.
                        setForecastWindow((prev) =>
                          HORIZON_PRESETS.some((p) => p.days === prev)
                            ? prev
                            : WINDOW_DEFAULTS[v].forecastWindow
                        );
                      }}
                    >
                      {WINDOW_LABELS[v] ?? `${v} days`}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </InputGroup>

            <Button
              color="primary"
              className="find-match-btn"
              onClick={onSubmit}
              disabled={submitDisabled}
            >
              {fetching ? (
                <>
                  <Spinner size="sm" />
                  <span>Finding...</span>
                </>
              ) : (
                "Find Matches"
              )}
            </Button>
          </div>

          <div className="shape-forward-horizon">
            <span className="shape-forward-horizon-label">Forward horizon</span>
            <ButtonGroup size="sm">
              <Button
                color={forecastWindow === windowDefaultHorizon ? "primary" : "outline-secondary"}
                onClick={() => setForecastWindow(windowDefaultHorizon)}
              >
                Default ({windowDefaultHorizon}d)
              </Button>
              {HORIZON_PRESETS.map((p) => (
                <Button
                  key={p.days}
                  color={forecastWindow === p.days ? "primary" : "outline-secondary"}
                  onClick={() => setForecastWindow(p.days)}
                >
                  {p.label}
                </Button>
              ))}
            </ButtonGroup>
            <span className="shape-forward-horizon-note">
              A longer horizon needs more history after each analog, so analogs whose window ends in
              the last {forecastWindow} sessions drop out — the analog count falls as you extend it.
            </span>
          </div>

          <button
            className="advanced-toggle"
            onClick={() => setShowAdvanced((prev) => !prev)}
            type="button"
          >
            <span className={`advanced-toggle-icon ${showAdvanced ? "open" : ""}`}>&#9656;</span>
            Advanced Filters
          </button>

          {showAdvanced && (
            <div className="advanced-filters">
              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="shape-min-price"
                  data-tooltip-content="Skip analogs where the stock was trading below this price - filters out penny-stock noise"
                >
                  Min Price
                </InputGroupText>
                <ReactTooltip id="shape-min-price" />
                <input
                  className="custom-number-input"
                  type="number"
                  value={minPrice}
                  min="0"
                  max="9999"
                  onChange={priceHandler}
                />
              </InputGroup>

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="shape-max-change"
                  data-tooltip-content="Drop analogs that moved more than this % afterward, so one extreme outlier doesn't skew the average outcome"
                >
                  Exclude Moves Over
                </InputGroupText>
                <ReactTooltip id="shape-max-change" />
                <input
                  className="custom-range-input"
                  type="range"
                  min="0"
                  max="200"
                  value={maxChange}
                  onChange={maxChangeHandler}
                />
                <InputGroupText className="range-value">{maxChange}%</InputGroupText>
              </InputGroup>

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="shape-max-matches"
                  data-tooltip-content="The most similar historical patterns to pull in, ranked by similarity score"
                >
                  Max Matches
                </InputGroupText>
                <ReactTooltip id="shape-max-matches" />
                <input
                  className="custom-number-input"
                  type="number"
                  value={numMatches}
                  min="1"
                  max="101"
                  onChange={numMatchesHandler}
                />
              </InputGroup>

              <InputGroup className="custom-input-group">
                <InputGroupText
                  data-tooltip-id="shape-forecast-window"
                  data-tooltip-content="How many trading days after the pattern to measure the outcome - e.g. 21 means 'what happened a month later'"
                >
                  Forecast Window (days)
                </InputGroupText>
                <ReactTooltip id="shape-forecast-window" />
                <input
                  className="custom-number-input"
                  type="number"
                  value={forecastWindow}
                  min="1"
                  max={MAX_FORECAST_WINDOW}
                  onChange={forecastWindowHandler}
                />
              </InputGroup>
            </div>
          )}

          {error && (
            <Alert color={errorColor} className="error-alert">
              {error}
            </Alert>
          )}

          {noResults && !error && (
            <Alert color="warning" className="mt-3 mb-0">
              No matches found for this shape ({windowValue}-day window, min score: {score}%,{" "}
              {forecastWindow}-day forward horizon). Try{" "}
              {mode === "draw"
                ? "redrawing it"
                : mode === "upload"
                  ? "a different image"
                  : "another security or lookback"}
              , lowering <strong>Min Score</strong>, shortening the forward horizon, or adjusting{" "}
              <strong>Advanced Filters</strong>.
            </Alert>
          )}
        </CardBody>
      </Card>
    </Container>
  );
}
